import {
    Badge,
    Box,
    Flex,
    Grid,
    GridItem,
    HStack,
    Icon,
    Image,
    Link,
    Spacer,
    Stack,
    Text,
} from "@chakra-ui/react";
import { BuildingsIcon } from "@phosphor-icons/react/dist/ssr";
import { useMemo } from "react";
import { LazyLoadComponent } from "react-lazy-load-image-component";

import { brandColor } from "@/utilities/color-presets/colorConfig";
import { wrapBgImage } from "@/utilities/helpers/wrapBgImage";

function DevsCard(props, isSearchCard) {
    const {
        id,
        title,
        subTitle,
        location,
        link,
        imageUrl,
        propertyType = null,
        isCentered,
        referred,
        industrial,
        groundSizeMin,
        groundSizeMax,
        bedroomsMin,
        bedroomsMax,
        session,
        online_sale,
        operation_type,
        saveDevFavorites,
        development_id,
        userFavorites,
        isForMap = false,
        isDevelopmentSheet = false,
    } = props;
    const redirectNewPage = () => {
        if (referred) {
            window.open(link, "_blank");
        }
    };

    //Validar si el desarrollo es favorito
    const isFavorite = useMemo(() => {
        return userFavorites?.some((fav) => fav.development_id === development_id);
    }, [userFavorites]);

    return (
        <LazyLoadComponent id={id}>
            {isSearchCard ? (
                <Box
                    boxShadow="lg"
                    display="flex"
                    w={
                        isForMap
                            ? { base: "250px", md: "350px" }
                            : isDevelopmentSheet
                              ? { base: "335px", md: "300px" }
                              : "364px"
                    }
                    onClick={() => redirectNewPage()}
                    h={isForMap ? "450px" : isDevelopmentSheet ? "400px" : "560px"}
                    as="div"
                    animate={
                        isCentered
                            ? {
                                  scale: 1.1,
                              }
                            : {}
                    }
                    transition="0.2s linear"
                    borderRadius="10px"
                    bgImage={wrapBgImage(
                        encodeURI(
                            imageUrl.replace(
                                "https://storage-vision.s3.us-east-2.amazonaws.com/",
                                "https://media.grupoguia.mx/vision/"
                            )
                        )
                    )}
                    bgPosition="center"
                    bgSize="cover"
                    bgRepeat="no-repeat"
                    cursor="pointer"
                >
                    <Flex direction="column" w="100%">
                        {/* Single row pinned to the top of the image area:
                            badges on the left, kind marker + favorite on the
                            right. Left group wraps when narrow; right group
                            stays anchored as a corner control. */}
                        <Flex justify="space-between" align="flex-start" gap={2} px={3} mt={4}>
                            {/* flexWrap on the left group so badges break to
                                a new row when the card narrows; right group
                                (kind marker + favorite) stays anchored as a
                                top-right corner control. */}
                            <Flex gap={2} align="center" flexWrap="wrap" minW={0} flex="1 1 auto">
                                {operation_type?.map((operation, idx) => (
                                    <Badge
                                        key={idx}
                                        px={2}
                                        py={1.5}
                                        color="gray.100"
                                        bg="accent.500"
                                        fontWeight="bold"
                                        boxShadow="lg"
                                        borderRadius="lg"
                                        fontSize="xs"
                                        textTransform="capitalize"
                                    >
                                        {operation === "SALE"
                                            ? "Venta"
                                            : operation === "PRESALE"
                                              ? "Preventa"
                                              : "Preventa"}
                                    </Badge>
                                ))}
                                {online_sale && (
                                    <Badge
                                        px={3}
                                        py={1.5}
                                        color="gray.100"
                                        bg="#5AC727"
                                        fontWeight="700"
                                        boxShadow="lg"
                                        borderRadius="lg"
                                        fontSize="xs"
                                        textTransform="capitalize"
                                    >
                                        Aparta Online
                                    </Badge>
                                )}
                            </Flex>
                            <Flex align="center" gap={2} flexShrink={0}>
                                <Box
                                    bg="brand.500"
                                    color="accent.500"
                                    w="32px"
                                    h="32px"
                                    borderRadius="full"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    boxShadow="md"
                                    pointerEvents="none"
                                    flexShrink={0}
                                >
                                    <Icon boxSize={4}>
                                        <BuildingsIcon weight="fill" />
                                    </Icon>
                                </Box>
                                {session && (
                                    <Image
                                        src={
                                            isFavorite
                                                ? "https://media.grupoguia.mx/assets/apps-icons/favorite_active.svg"
                                                : "https://media.grupoguia.mx/assets/apps-icons/favorite.svg"
                                        }
                                        alt="favorite"
                                        maxH={!isFavorite ? "5vh" : "3.5vh"}
                                        cursor="pointer"
                                        borderRadius="full"
                                        transition="0.2s ease-in-out"
                                        _hover={{
                                            filter: "brightness(90%)",
                                            transform: "scale(1.1)",
                                        }}
                                        _active={{
                                            filter: "brightness(80%)",
                                            transform: "scale(0.95)",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            saveDevFavorites(null, development_id);
                                        }}
                                    />
                                )}
                            </Flex>
                        </Flex>
                        <Spacer />
                        <Flex direction="column" w="full" h="10rem">
                            <Box
                                px="3"
                                textAlign="left"
                                paddingTop={industrial ? "7" : "3"}
                                paddingBottom={industrial ? "7" : "0"}
                                bg="#fff"
                                shadow={industrial ? "lg" : "md"}
                                height="100%"
                                display="flex"
                                flexDirection="column"
                            >
                                <Text
                                    textTransform="capitalize"
                                    color="accent.500"
                                    lineClamp="1"
                                    textAlign="center"
                                    fontWeight="600"
                                    fontSize="md"
                                    letterSpacing="wide"
                                >
                                    {title.toLowerCase()}
                                </Text>
                                <Stack py={2} h="3rem">
                                    <Text
                                        fontSize="xs"
                                        color="#4B4B4B"
                                        fontWeight={400}
                                        textTransform="capitalize"
                                        lineClamp={2}
                                        textAlign="center"
                                        px={3}
                                    >
                                        {location}
                                    </Text>
                                </Stack>
                                <HStack spacing={8} mt={2} mb={3} justifyContent="center">
                                    {groundSizeMin !== groundSizeMax ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/cinta-metrica.svg"
                                                alt="simple icon"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {groundSizeMin > 0 ? (
                                                    <>
                                                        {groundSizeMin} m&sup2; a {groundSizeMax}{" "}
                                                        m&sup2;
                                                    </>
                                                ) : (
                                                    <>{groundSizeMax} m&sup2;</>
                                                )}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                    {bedroomsMin !== bedroomsMax ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/cama.svg"
                                                alt="simple icon"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {bedroomsMin > 0 ? (
                                                    <>
                                                        {bedroomsMin} a {bedroomsMax}
                                                    </>
                                                ) : (
                                                    <>{bedroomsMax}</>
                                                )}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                </HStack>
                                <Link
                                    alignSelf="center"
                                    mt="auto"
                                    mb={1}
                                    display="block"
                                    fontSize="sm"
                                    lineHeight="normal"
                                    fontWeight={600}
                                    href={link}
                                    target="_blank"
                                    color="#000"
                                    external
                                    pb={2}
                                    _hover={{
                                        textDecoration: "none",
                                        ...brandColor,
                                    }}
                                >
                                    <span>{subTitle}</span>
                                </Link>
                            </Box>
                        </Flex>
                        <Box
                            fontSize="sm"
                            textAlign="center"
                            bg="#DFDFDF"
                            borderBottomRadius="10px"
                            py={2}
                        >
                            {propertyType}
                        </Box>
                    </Flex>
                </Box>
            ) : (
                <Box
                    boxShadow="lg"
                    display="flex"
                    w={{ base: "335px", md: "300px" }}
                    onClick={() => redirectNewPage()}
                    h="400px"
                    as="div"
                    animate={
                        isCentered
                            ? {
                                  scale: 1.1,
                              }
                            : {}
                    }
                    transition="0.2s linear"
                    borderRadius="10px"
                    bgImage={wrapBgImage(imageUrl)}
                    bgPosition="center"
                    bgSize="cover"
                    bgRepeat="no-repeat"
                    cursor="pointer"
                >
                    <Flex direction="column" w="100%">
                        <Flex direction="column" width="full" align="end" position="relative">
                            <Grid
                                templateColumns="repeat(3, 1fr)"
                                gap={2}
                                px={3}
                                w="full"
                                alignItems="center"
                                mt={5}
                            >
                                {operation_type?.map((operation, idx) => {
                                    return (
                                        <GridItem key={idx}>
                                            <Badge
                                                px={2}
                                                py={1.5}
                                                color="gray.100"
                                                bg="accent.500"
                                                fontWeight="bold"
                                                boxShadow="lg"
                                                borderRadius="lg"
                                                fontSize="xs"
                                                textTransform="capitalize"
                                                display="inline-flex"
                                                alignItems="center"
                                                gap={1.5}
                                            >
                                                {operation !== "SALE" && (
                                                    <Icon boxSize={3.5}>
                                                        <BuildingsIcon weight="fill" />
                                                    </Icon>
                                                )}
                                                {operation === "SALE"
                                                    ? "Venta"
                                                    : operation === "PRESALE"
                                                      ? "Preventa"
                                                      : "Preventa"}
                                            </Badge>
                                        </GridItem>
                                    );
                                })}
                                {online_sale && (
                                    <GridItem>
                                        <Badge
                                            px={3}
                                            py={1.5}
                                            color="gray.100"
                                            bg="#5AC727"
                                            fontWeight="700"
                                            boxShadow="lg"
                                            borderRadius="lg"
                                            fontSize="xs"
                                            textTransform="capitalize"
                                        >
                                            Aparta Online
                                        </Badge>
                                    </GridItem>
                                )}
                            </Grid>
                            {session && (
                                <Image
                                    src={
                                        isFavorite
                                            ? "https://media.grupoguia.mx/assets/apps-icons/favorite_active.svg"
                                            : "https://media.grupoguia.mx/assets/apps-icons/favorite.svg"
                                    }
                                    alt="favorite"
                                    maxH="5vh"
                                    cursor="pointer"
                                    borderRadius="full"
                                    transition="0.2s ease-in-out"
                                    _hover={{ filter: "brightness(90%)", transform: "scale(1.1)" }}
                                    _active={{
                                        filter: "brightness(80%)",
                                        transform: "scale(0.95)",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        saveDevFavorites(null, development_id);
                                    }}
                                    position="absolute"
                                    top={5}
                                    right={2}
                                />
                            )}
                        </Flex>
                        <Spacer />
                        <Flex direction="column" w="full" h="10rem">
                            <Box
                                px="3"
                                textAlign="left"
                                paddingTop={industrial ? "7" : "3"}
                                paddingBottom={industrial ? "7" : "0"}
                                bg="#fff"
                                shadow={industrial ? "lg" : "md"}
                                height="100%"
                                display="flex"
                                flexDirection="column"
                            >
                                <Link
                                    _hover={{
                                        textDecoration: "none",
                                        ...brandColor,
                                    }}
                                    textAlign="center"
                                    fontWeight="600"
                                    fontSize="md"
                                    letterSpacing="wide"
                                    color="accent.500"
                                    external
                                >
                                    <Text
                                        textTransform="capitalize"
                                        color="accent.500"
                                        lineClamp="1"
                                    >
                                        {title.toLowerCase()}
                                    </Text>
                                </Link>
                                <Stack py={2} h="3rem">
                                    <Text
                                        fontSize="xs"
                                        color="#4B4B4B"
                                        fontWeight={400}
                                        textTransform="capitalize"
                                        lineClamp={2}
                                        textAlign="center"
                                        px={3}
                                    >
                                        {location}
                                    </Text>
                                </Stack>
                                <HStack spacing={8} mt={2} mb={3} justifyContent="center">
                                    {groundSizeMin !== groundSizeMax ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/cinta-metrica.svg"
                                                alt="simple icon"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {groundSizeMin > 0 ? (
                                                    <>
                                                        {groundSizeMin} m&sup2; a {groundSizeMax}{" "}
                                                        m&sup2;
                                                    </>
                                                ) : (
                                                    <>{groundSizeMax} m&sup2;</>
                                                )}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                    {bedroomsMin !== bedroomsMax ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/cama.svg"
                                                alt="simple icon"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {bedroomsMin > 0 ? (
                                                    <>
                                                        {bedroomsMin} a {bedroomsMax}
                                                    </>
                                                ) : (
                                                    <>{bedroomsMax}</>
                                                )}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                </HStack>
                                <Link
                                    alignSelf="center"
                                    mt="auto"
                                    mb={1}
                                    display="block"
                                    fontSize="sm"
                                    lineHeight="normal"
                                    fontWeight={600}
                                    href={link}
                                    target="_blank"
                                    color="#000"
                                    external
                                    pb={2}
                                    _hover={{
                                        textDecoration: "none",
                                        color: "brand.600",
                                        ...brandColor,
                                    }}
                                >
                                    <span>{subTitle}</span>
                                </Link>
                            </Box>
                        </Flex>
                        <Box
                            fontSize="sm"
                            textAlign="center"
                            bg="#DFDFDF"
                            borderBottomRadius="10px"
                            py={2}
                        >
                            {propertyType}
                        </Box>
                    </Flex>
                </Box>
            )}
        </LazyLoadComponent>
    );
}

export default DevsCard;
